import {appRatingsApi} from 'modules/appRatings'
import {preloadAppRatings} from 'modules/appRatings/preload'
import {seoApi} from 'modules/seoApi'
import {
  preloadSeoPopularLinks,
  preloadSeoTopHotels
} from 'modules/seoApi/preload'
import {preloadTrustpilot} from 'modules/trustpilot/preload'
import {GetServerSideProps} from 'next'
import {omit, pick} from 'ramda'
import {initializeStore, RootState} from 'store'
import {Toggle} from 'types/Toggle'
import {
  baseGetServerSideProps,
  CommonPageProps
} from 'utils/server/defaultGetServerSideProps'
import {
  hasPreloadedReduxState,
  isRedirectOrNotFound
} from 'utils/server/getServerSidePropsUtils'
import {logPreloadError} from 'utils/server/logPreloadError'
import {incrementMetric} from 'utils/server/metrics'
import {serverSideTimeouts} from 'utils/server/serverSideTimeouts'
import HomeView from 'views/home'

import {Page} from '@vio/core/src/analytics/types/Events'
import {getSapiClient} from '@vio/core/src/sapi/services'
import {trustpilotApi} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {noop} from '@vio/core/src/utils/function'

export default HomeView

const sanitizeState = pick([
  'meta',
  'experiments',
  'seoApi',
  'appRatingsApi',
  'trustpilotApi'
])

const reportAppRatingsFailure = (error: unknown) => {
  logPreloadError('Error preloading app ratings:', error)
  incrementMetric('ssr.home.preload_error', {preload: 'app_ratings'})
}

const reportTrustpilotFailure = (error: unknown) => {
  logPreloadError('Error preloading Trustpilot business unit:', error)
  incrementMetric('ssr.home.preload_error', {preload: 'trustpilot'})
}

export const getServerSideProps: GetServerSideProps<
  CommonPageProps
> = async context => {
  const baseProps = await baseGetServerSideProps({page: Page.Home})(context)

  if (
    isRedirectOrNotFound(baseProps) ||
    !hasPreloadedReduxState(baseProps.props)
  ) {
    return baseProps
  }

  const store = initializeStore(
    baseProps.props.preloadedReduxState as RootState,
    {
      toggle: noop as Toggle,
      getSapiClient: noop as typeof getSapiClient
    }
  )

  try {
    // On a CDN miss the ratings route waits on itunes.apple.com and Google Play,
    // so it gets its own budget. Missing it must not cost us the SEO preloads.
    const appRatingsPreload = serverSideTimeouts
      .appRatingsPreload(store.dispatch(preloadAppRatings()))
      .then(
        ({isError, error}) => {
          // initiate() resolves on an API error rather than rejecting, so a 500
          // only shows up here.
          if (isError) reportAppRatingsFailure(error)

          // A rejected entry carries no fulfilledTimeStamp, so the client
          // refetches it on mount and the slice is still worth shipping.
          return true
        },
        error => {
          reportAppRatingsFailure(error)
          return false
        }
      )

    const trustpilotPreload = preloadTrustpilot(store, reportTrustpilotFailure)

    await serverSideTimeouts.seoPreload(
      Promise.all([
        store.dispatch(preloadSeoPopularLinks()),
        store.dispatch(preloadSeoTopHotels())
      ])
    )

    const hasAppRatings = await appRatingsPreload
    const hasTrustpilot = await trustpilotPreload
    const state = sanitizeState(store.getState())

    return {
      ...baseProps,
      props: {
        ...baseProps.props,
        // A query that missed its budget is still pending in the slice, and RTK
        // Query will not start a request for an entry it thinks is in flight, so
        // shipping it would leave the client on the skeleton for good.
        preloadedReduxState: omit(
          [
            ...(hasAppRatings ? [] : ['appRatingsApi']),
            ...(hasTrustpilot ? [] : ['trustpilotApi'])
          ],
          state
        )
      }
    }
  } catch (error) {
    logPreloadError('Error preloading home:', error)
    incrementMetric('ssr.home.preload_error')
    return baseProps
  } finally {
    // Avoid memory leaks from preloads - https://redux-toolkit.js.org/rtk-query/usage/server-side-rendering#server-side-rendering-with-nextjs
    store.dispatch(seoApi.util.resetApiState())
    store.dispatch(appRatingsApi.util.resetApiState())
    store.dispatch(trustpilotApi.util.resetApiState())
  }
}
